import type { AssetDto } from '@/api/WhisApi/generated';
import { useAssetsGetAsset } from '@/api/WhisApi/generated/assets/assets';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { Button } from '@components/Button/Button';
import { showToast } from '@components/Toast/toast.service';
import { Ionicons } from '@expo/vector-icons';
import type { ReactElement } from 'react';
import { useCallback } from 'react';
import { Linking } from 'react-native';
import { SizableText, useTheme, XStack, YStack } from 'tamagui';
import {
  getAssetDisplayName,
  getAssetIconName,
  isImageAsset,
} from '../reportDetailSupportingDocuments.utils';
import { openAssetUrl } from '../reportDetailAssetOpen.utils';

interface SupportingDocumentRowProps {
  asset: AssetDto;
  onPreviewImage: (url: string, name: string) => void;
}

export const SupportingDocumentRow = ({
  asset,
  onPreviewImage,
}: SupportingDocumentRowProps): ReactElement => {
  const { t } = useAppTranslation();
  const theme = useTheme();
  const iconColor = theme.color?.val ?? '#27282A';
  const { refetch, isFetching } = useAssetsGetAsset(asset.fileName, {
    query: { enabled: false },
  });

  const handleOpenAsset = useCallback(async (): Promise<void> => {
    try {
      await openAssetUrl({
        fetchAssetUrl: refetch,
        onUrlResolved: async (assetUrl) => {
          if (isImageAsset(asset.fileName)) {
            onPreviewImage(assetUrl, getAssetDisplayName(asset));
            return;
          }

          await Linking.openURL(assetUrl);
        },
      });
    } catch (error) {
      console.error('Failed to open supporting document', error);
      showToast({
        type: 'error',
        title: t('reportDetail.toasts.openSupportingDocumentError'),
      });
    }
  }, [asset, onPreviewImage, refetch, t]);

  return (
    <XStack alignItems="center" justifyContent="space-between" gap="$3">
      <XStack alignItems="center" gap="$3" flex={1}>
        <YStack
          backgroundColor="$tertiary50"
          padding="$2"
          borderRadius="$circle"
        >
          <Ionicons
            name={getAssetIconName(asset.fileName)}
            size={18}
            color={iconColor}
          />
        </YStack>
        <YStack flex={1}>
          <SizableText size="$2" fontFamily="$bold" numberOfLines={1}>
            {getAssetDisplayName(asset)}
          </SizableText>
        </YStack>
      </XStack>

      <Button
        variant="outlined"
        icon={<Ionicons name="eye-outline" size={15} color={iconColor} />}
        onPress={handleOpenAsset}
        loading={isFetching}
        padding="$2"
        size="$2"
      />
    </XStack>
  );
};
